import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return value;
  }
};

const formatSla = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return `${Number(value).toFixed(1)}%`;
};

const statusColor = (status) => {
  if (status === 'UP') return 'success';
  if (status === 'DEGRADED') return 'warning';
  if (status === 'DOWN') return 'error';
  return 'default';
};

const gatewayTypeLabel = (value) => {
  if (value === 'BS') return 'Базовая станция';
  if (value === 'USPD') return 'УСПД';
  return value || '—';
};

const lorawanLinkMeta = (row) => {
  const status = row?.lorawan_link_status;
  if (status === 'ok') return { label: 'LoRaWAN: работает', color: 'success' };
  if (status === 'warning') return { label: 'LoRaWAN: замечания', color: 'warning' };
  if (status === 'suggested') return { label: 'Найдено совпадение', color: 'info' };
  if (status === 'linked_no_data' || status === 'no_data') return { label: 'Связана, данных нет', color: 'default' };
  return { label: 'Не связана', color: 'default' };
};

const pathIssueMeta = (row) => {
  const focus = row?.path_issue_focus;
  if (focus === 'gateway') return { label: row.path_issue_label || 'Шлюз', color: 'error' };
  if (focus === 'devices') return { label: row.path_issue_label || 'Счётчики', color: 'warning' };
  if (focus === 'mixed') return { label: row.path_issue_label || 'Смешанно', color: 'warning' };
  if (focus === 'ok') return { label: row.path_issue_label || 'OK', color: 'success' };
  return { label: row?.path_issue_label || 'Нет данных', color: 'default' };
};

const actionButtonSx = {
  width: 38,
  height: 38,
  borderRadius: 2.5,
  border: '1px solid rgba(148,163,184,0.18)',
  background: 'rgba(255,255,255,0.96)',
  color: '#475569',
  boxShadow: '0 10px 22px rgba(15,23,42,0.08)',
  transition: 'all 160ms ease',
  '&:hover': {
    background: '#ffffff',
    color: '#0f172a',
    transform: 'translateY(-1px)',
    boxShadow: '0 14px 26px rgba(15,23,42,0.12)',
  },
};

function RowActions({ row, canManage, checkingId, onOpen, onEdit, onCheck }) {
  return (
    <Box
      className="gateway-row-actions"
      sx={{
        width: 0,
        minWidth: 0,
        overflow: 'hidden',
        opacity: 0,
        pointerEvents: 'none',
        transform: 'translateX(10px)',
        transition:
          'width 180ms ease, min-width 180ms ease, transform 180ms ease, opacity 180ms ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Stack spacing={0.75} sx={{ py: 0.5 }}>
        <Tooltip title="Открыть" placement="right">
          <IconButton sx={actionButtonSx} onClick={() => onOpen?.(row)}>
            <VisibilityRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={checkingId === row.id ? 'Проверяем…' : 'Проверить'} placement="right">
          <span>
            <IconButton
              sx={actionButtonSx}
              onClick={() => onCheck?.(row)}
              disabled={checkingId === row.id}
            >
              {checkingId === row.id ? (
                <CircularProgress size={17} />
              ) : (
                <SyncRoundedIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {canManage && (
          <Tooltip title="Редактировать" placement="right">
            <IconButton
              sx={{
                ...actionButtonSx,
                background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                color: '#fff',
                borderColor: 'rgba(37,99,235,0.24)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8, #1e40af)',
                  color: '#fff',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 14px 28px rgba(37,99,235,0.24)',
                },
              }}
              onClick={() => onEdit?.(row)}
            >
              <EditRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

export default function GatewaysTable({
  rows = [],
  slaMap = {},
  slaWindowHours = 24,
  loading = false,
  onOpen,
  onEdit,
  onCheck,
  onLinkLoRaWan,
  checkingId = null,
  canManage = false,
  complexNameMap = {},
  buildingNameMap = {},
}) {
  return (
    <Box sx={{ overflow: 'hidden' }}>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          overflowX: 'auto',
          overflowY: 'hidden',
          border: '1px solid rgba(15,23,42,0.08)',
          boxShadow: '0 14px 30px rgba(15, 23, 42, 0.06)',
          background: '#fff',
        }}
      >
        <Table size="small" sx={{ minWidth: 920 }}>
          <TableHead>
            <TableRow
              sx={{
                background: 'rgba(248,250,252,0.95)',
                '& th': {
                  fontWeight: 700,
                  color: 'text.secondary',
                  borderBottomColor: 'rgba(15,23,42,0.08)',
                },
              }}
            >
              <TableCell>Шлюз</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Диагноз</TableCell>
              <TableCell>SLA ({slaWindowHours}ч)</TableCell>
              <TableCell>Мониторинг</TableCell>
              <TableCell>Сигналы</TableCell>
              <TableCell>Размещение</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading &&
              Array.from({ length: 6 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`} hover>
                  <TableCell colSpan={7}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 0.5 }}>
                      <Skeleton variant="rounded" width={42} height={42} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="32%" height={20} />
                        <Skeleton width="58%" height={18} />
                      </Box>
                      <Skeleton width={92} height={26} />
                      <Skeleton width={84} height={18} />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}

            {!loading &&
              rows.map((row) => {
                const sla = slaMap[String(row.id)];
                const complexName = complexNameMap[row.residential_complex_id] || '—';
                const buildingName = buildingNameMap[row.building_id] || '—';
                const lorawanMeta = lorawanLinkMeta(row);
                const pathMeta = pathIssueMeta(row);
                const canLinkSuggested =
                  canManage && row.gateway_type === 'BS' && row.lorawan_suggested_gateway_id && !row.lorawan_gateway_id;
                const devicesTotal = Number(row.devices_total || 0);
                const devicesProblem = Number(row.devices_problem || 0);

                return (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      position: 'relative',
                      transition: 'transform 180ms ease, box-shadow 180ms ease',
                      '& td': {
                        verticalAlign: 'top',
                        borderBottomColor: 'rgba(15,23,42,0.06)',
                        background: '#fff',
                      },
                      '&:hover': {
                        transform: 'translateX(-6px)',
                        boxShadow: '0 14px 28px rgba(15,23,42,0.05)',
                        zIndex: 3,
                      },
                      '&:hover .gateway-row-actions': {
                        opacity: 1,
                        pointerEvents: 'auto',
                        width: canManage ? 54 : 54,
                        minWidth: canManage ? 54 : 54,
                        transform: 'translateX(0)',
                      },
                    }}
                  >
                    <TableCell sx={{ minWidth: 240 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.name || 'Без названия'}
                        </Typography>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap">
                          <Chip size="small" variant="outlined" label={gatewayTypeLabel(row.gateway_type)} />
                          {row.mac_address ? (
                            <Chip size="small" variant="outlined" label={row.mac_address} />
                          ) : null}
                        </Stack>
                        {row.location || row.address ? (
                          <Typography variant="caption" color="text.secondary">
                            {[row.location, row.address].filter(Boolean).join(' · ')}
                          </Typography>
                        ) : null}
                        {row.gateway_type === 'BS' && row.lorawan_gateway_id ? (
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748B', fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}
                          >
                            ID: {row.lorawan_gateway_id}
                          </Typography>
                        ) : null}
                        {row.gateway_type === 'BS' && row.lorawan_suggested_gateway_id && !row.lorawan_gateway_id ? (
                          <Typography
                            variant="caption"
                            sx={{ color: '#64748B', fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}
                          >
                            ID: {row.lorawan_suggested_gateway_id}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ minWidth: 140 }}>
                      <Stack spacing={0.75}>
                        <Chip size="small" color={statusColor(row.status)} label={row.status || '—'} />
                        <Typography variant="caption" color="text.secondary">
                          Проверка: {formatDate(row.last_seen_at)}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ minWidth: 150 }}>
                      <Stack spacing={0.5}>
                        <Tooltip title={row.path_issue_reason || ''} arrow disableHoverListener={!row.path_issue_reason}>
                          <Chip size="small" color={pathMeta.color} label={pathMeta.label} />
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          {devicesTotal
                            ? `${devicesProblem}/${devicesTotal} проблемных`
                            : 'Нет приборов'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ minWidth: 120 }}>
                      <Stack spacing={0.35}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatSla(sla?.sla_percent)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Окно {slaWindowHours}ч
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ minWidth: 170 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{row.monitoring_mode || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Heartbeat: {row.heartbeat_interval_sec ? `${row.heartbeat_interval_sec} сек` : 'авто'}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell sx={{ minWidth: 220 }}>
                      <Stack spacing={0.65}>
                        <Tooltip title={row.host || '—'}>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}
                          >
                            {row.host || '—'}
                          </Typography>
                        </Tooltip>
                        <Typography variant="caption" color="text.secondary">
                          MQTT: {row.mqtt_topic || '—'}
                        </Typography>
                        {row.gateway_type === 'BS' ? (
                          <Stack spacing={0.45}>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={lorawanMeta.color}
                              label={row.lorawan_link_status_label || lorawanMeta.label}
                              sx={{ width: 'fit-content', fontWeight: 800 }}
                            />
                            {canLinkSuggested ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onLinkLoRaWan?.(row)}
                                sx={{
                                  width: 'fit-content',
                                  minHeight: 24,
                                  px: 1,
                                  py: 0.1,
                                  borderRadius: 1.5,
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                Связать
                              </Button>
                            ) : null}
                            {row.lorawan_linked && row.lorawan_health_status ? (
                              <Typography variant="caption" color="text.secondary">
                                Приборов: {row.lorawan_unique_devices_count ?? 0} · RSSI/SNR:{' '}
                                {row.lorawan_rssi_avg == null ? '—' : Number(row.lorawan_rssi_avg).toFixed(1)} /{' '}
                                {row.lorawan_snr_avg == null ? '—' : Number(row.lorawan_snr_avg).toFixed(1)}
                              </Typography>
                            ) : null}
                          </Stack>
                        ) : null}
                      </Stack>
                    </TableCell>

                    <TableCell
                      sx={{
                        minWidth: 200,
                        pr: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'stretch',
                          justifyContent: 'space-between',
                          gap: 1,
                          minHeight: canManage ? 118 : 82,
                        }}
                      >
                        <Stack spacing={0.5} sx={{ pr: 0.5, justifyContent: 'center', minWidth: 0, flex: 1 }}>
                          <Typography variant="body2">{complexName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Дом: {buildingName}
                          </Typography>
                        </Stack>

                        <RowActions
                          row={row}
                          canManage={canManage}
                          checkingId={checkingId}
                          onOpen={onOpen}
                          onEdit={onEdit}
                          onCheck={onCheck}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Stack spacing={1} alignItems="center">
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Нет данных
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Попробуйте изменить фильтры или добавить новый шлюз.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
